import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import clsx from 'clsx';

import { Dicon, Button, Image } from 'edcom-ui';
import content from '../../../services/content';
import memberService from '../../../services/memberApi';

import AccountContext, { MemberType } from '../../../services/AccountContext';
import { CollectionsProvider } from '../../../services/CollectionsContext';
import { openSignup } from '../../../services/regModal';

import Head from '../../../components/head';
import Breadcrumbs from '../../../components/Breadcrumbs';
import CategoryTree from '../../../components/CategoryTree/CategoryTree';
import RelatedGuidedLesson from '../../../components/RelatedGuidedLessons';

import styles from './worksheet.module.less';
import { openPayWall } from '../../../services/payWallModal';
import Credits from '../../../components/Credits';
import WorksheetActions from '../../../components/worksheet/WorksheetActions';
import Article from '../../../components/StructuredData/Article';
import track, { trackGA } from '../../../services/track';

import Error from '../../../components/Error';
import AdminLinks from '../../../components/AdminLinks/AdminLinks';
import AppLink from '../../../components/links/AppLink';

const RelatedLearningResources = dynamic(
  () => import('../../../components/RelatedLearningResources'),
  { ssr: false }
);

const ShareOptions = dynamic(() => import('../../../components/ShareOptions'), {
  ssr: false,
});

const Worksheet = ({
  id,
  title,
  urlName,
  metaTitle = '',
  metaDescription = '',
  indexed = false,
  text = '',
  updatedAt,
  createdAt,
  standards = [],
  grades = [],
  subjects = [],
  mainImage = null,
  lessonPlan = null,
  guidedLesson = null,
  slideshow = null,
  downloadHref,
  answersheetHref = null,
  error = false,
  errorCode,
}) => {
  const { ready, memberId, isPremium, isType } = useContext(AccountContext);

  const [refreshCredits, setRefreshCredits] = useState(false);

  let actionButtonText = 'Download Worksheet';
  if (memberId && isType(MemberType.Parent)) {
    actionButtonText = 'Print Worksheet';
  } else if (ready && !memberId) {
    actionButtonText = 'Download Free Worksheet';
  }

  // Sort by length of internalName. This puts the more general subjects before more specific ones
  subjects.sort((a, b) => a.internalName.length - b.internalName.length);

  const relatedGuidedLesson = guidedLesson ? (
    <RelatedGuidedLesson {...guidedLesson} />
  ) : null;

  const trackDownload = ({
    button = null,
    googleDrive = false,
    answerKey = false,
  }) => {
    track('Downloaded Content', {
      contentId: id,
      contentType: answerKey ? 'worksheet-answerkey' : 'worksheet',
      name: title,
      section: 'Worksheet Detail',
      button,
      method: googleDrive ? 'google-drive' : '',
    });
    trackGA('worksheet', answerKey ? 'download answerkey' : 'download');
  };

  const handleImageClick = async event => {
    // Same as the main download button, but different button name for analytics tracking
    await handleMainAction(event, 'image_hover');
  };

  const handleMainAction = async (event, button = 'main') => {
    if (!memberId) {
      event.preventDefault();
      openSignup('download', {
        signUp: { title: 'Join Education.com for Free Access to Worksheets' },
      });
      return;
    }

    if (!isPremium) {
      event.preventDefault();
      const canDownload = await memberService.checkCanDownload('worksheet', id);
      if (canDownload?.allowed === true) {
        window.open(downloadHref, '_blank', 'noopener');
        trackDownload({ button });
        setRefreshCredits(!refreshCredits);
      } else {
        openPayWall({
          contentId: id,
          contentType: 'worksheet',
          section: 'Worksheet Detail',
          feature: 'Worksheet Download',
          name: title,
          cid: '10.851',
        });
      }
    } else {
      trackDownload({ button });
    }

    setRefreshCredits(!refreshCredits);
  };

  // Record page view for popularity tracking
  useEffect(() => {
    content().recordView('worksheet', id);
  }, [id]);

  // SEO test to add grade/subject to the content type for 5th grade only
  let contentTypeDisplay = 'Worksheet';
  if (grades[0] && grades[0].urlName === 'fifth-grade') {
    if (subjects[0]) {
      // Get the 2nd level subject (e.g. if subjects[0] is math, this will match math.fractions)
      const regex = new RegExp(
        '^' + subjects[0].internalName.replace(/\./g, '\\.') + '\\.[^.]+$'
      );
      const match = subjects.filter(s => s.internalName.match(regex))[0];
      if (match) {
        contentTypeDisplay = match.name + ' ' + contentTypeDisplay;
      }
    }
    contentTypeDisplay = '5th Grade ' + contentTypeDisplay;
  }

  const errorData =
    error && errorCode === 404
      ? {
          description: (
            <>
              Return to our{' '}
              <AppLink href="/worksheets">worksheet collection</AppLink>
            </>
          ),
          heading: 'Worksheet not found',
        }
      : null;

  return (
    <>
      <Head
        description={metaDescription}
        index={indexed}
        image={mainImage}
        title={metaTitle}
      >
        <title>{metaTitle}</title>
        <meta property="og:type" content="educationdotcom:worksheet" />
        <Article
          createdAt={createdAt ? createdAt.replace(/\s+/, 'T') : ''}
          updatedAt={updatedAt ? updatedAt.replace(/\s+/, 'T') : null}
          path={`/worksheet/article/${urlName}/`}
          image={mainImage}
          title={title}
          description={metaDescription}
          contentType="worksheet"
        />
      </Head>
      <div
        className={clsx('page-width', 'typography-small', styles.detailContent)}
      >
        {error ? (
          <Error {...errorData} />
        ) : (
          <CollectionsProvider>
            <Breadcrumbs
              className={styles.breadcrumbs}
              title={title}
              contentType="Worksheets"
              grades={grades}
              subjects={subjects}
              path="worksheets"
            />
            <div>
              <h3 className={styles.contentTitle}>
                <Dicon name="worksheet" theme="dark" />{' '}
                <span className={styles.contentName}>{contentTypeDisplay}</span>
              </h3>
              <h1>{title}</h1>
              <ShareOptions
                name={title}
                imageSrc={mainImage}
                contentType={'worksheet'}
                contentId={id}
              />
            </div>
            <div className={styles.content}>
              <div className={styles.thumbnailContainer}>
                <div className={styles.thumbnail}>
                  <a
                    href={downloadHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleImageClick}
                  >
                    <Image src={mainImage} alt={title} lazy={false} />
                  </a>
                </div>
              </div>
              <div className={styles.contentDetailContainer}>
                <div dangerouslySetInnerHTML={{ __html: text }} />
                <Credits refreshCredits={refreshCredits} />
                <div className={styles.mainActionContainer}>
                  <Button
                    size="normal"
                    onClick={handleMainAction}
                    href={downloadHref}
                    target="_blank"
                    className={styles.mainActionButton}
                  >
                    {actionButtonText}
                  </Button>
                </div>
                <WorksheetActions
                  id={id}
                  title={title}
                  thumbnail={mainImage}
                  lessonPlan={lessonPlan}
                  slideshow={slideshow}
                  answersheetHref={answersheetHref}
                  trackDownload={trackDownload}
                  downloadHref={downloadHref}
                />
                <CategoryTree
                  resource={{
                    grades: grades,
                    subjects: subjects,
                    standards: standards,
                  }}
                />
              </div>
            </div>
            {relatedGuidedLesson}
            <RelatedLearningResources type="worksheet" id={id} />
          </CollectionsProvider>
        )}
      </div>
      <AdminLinks>
        <a
          href={`/secure/content/editor.php?nocache&type=worksheet&id=${id}`}
          target="_blank"
        >
          Edit Worksheet
        </a>
      </AdminLinks>
    </>
  );
};

Worksheet.getInitialProps = async ({ asPath, req, query }) => {
  const contentService = content(req);

  const { urlname } = query;
  try {
    const response = await contentService.getWorksheetDetails(urlname);

    response.pageKey = asPath;
    return response;
  } catch (e) {
    console.log('InitialProps Error', e);
    return {
      error: e,
      errorCode: e.statusCode,
      errorDescription: e.message,
    };
  }
};

const categoryShape = PropTypes.shape({
  urlName: PropTypes.string,
  internalName: PropTypes.string,
  name: PropTypes.string,
});

Worksheet.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  metaTitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  indexed: PropTypes.bool,
  text: PropTypes.string.isRequired,
  updatedAt: PropTypes.string,
  createdAt: PropTypes.string,
  standards: PropTypes.arrayOf(categoryShape),
  grades: PropTypes.arrayOf(categoryShape),
  subjects: PropTypes.arrayOf(categoryShape),
  mainImage: PropTypes.string,
  lessonPlan: PropTypes.string,
  guidedLesson: PropTypes.object,
  slideshow: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    count: PropTypes.number,
  }),
  downloadHref: PropTypes.string.isRequired,
  answersheetHref: PropTypes.string,
  error: PropTypes.bool,
  errorCode: PropTypes.number,
};

export default Worksheet;
